// The editor search copy the head server rendered a static page from, handed
// to the SPA inside the served shell. The SPA seeds its page-seo cache from it
// before the router's first load, so the first tab title is computed from the
// same overrides as the served `<title>`, with no second read that could
// disagree with the first or fail where the first succeeded.
//
// A leaf, like `seo-text.ts`: the head server imports it to write the seed,
// and must not pull the SPA's api client in with it.

import { STATIC_PAGE_PATHS } from '@leanin/shared/static-pages';
import { z } from 'zod';

export const PAGE_SEO_SEED_ID = 'page-seo-seed';

const PageSeoSeedSchema = z.object({
  pages: z.array(
    z.object({
      path: z.enum(STATIC_PAGE_PATHS),
      seoTitle: z.string().nullable(),
      seoDescription: z.string().nullable(),
    }),
  ),
});

export type PageSeoSeed = z.infer<typeof PageSeoSeedSchema>;

// The seed is input from another tier, so it is parsed here, at the boundary
// it crosses. Absent (any page other than a static one, or a shell served
// without an origin) and malformed both mean "no seed": the loaders then read
// the API themselves, which is what they do on every in-app navigation anyway.
export function parsePageSeoSeed(text: string | null): PageSeoSeed['pages'] | null {
  if (text === null) return null;
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    // Not JSON at all: the same answer as a seed of the wrong shape.
    return null;
  }
  const parsed = PageSeoSeedSchema.safeParse(json);
  return parsed.success ? parsed.data.pages : null;
}
